import { selectHighestRiskInteraction } from '../../utils/interactionUtils';
import { getMedicationDisplayName } from '../../utils/medicationUtils';

/**
 * The dashboard's per-stack interaction shape — the object both card types
 * read.
 *
 * This is the step the dashboard rebuild dropped. It used to live in the My
 * stacks tab that the rebuild deleted, which resolved each finding's
 * medication name before handing the interaction to a card; the rebuild
 * rewrote the mapping inline in `Dashboard/index.js` and left the name out, so
 * every consumer fell through to `Medication (<rxcui>)` (STACK-540).
 *
 * (The old tab is named by path in the ticket, not here: `dashboardRoutes.test.js`
 * bans any reference to that directory anywhere in `src/`.)
 *
 * It is a module rather than a closure inside the effect for one reason: the
 * two consumers are different components in different folders — `StackCard`
 * reads the card-level `medication_name`, `RecommendedStackCard` reads each
 * `allInteractions` entry — and a fix that fed one and not the other would
 * pass its own test while shipping the same bug on the other card. Both card
 * suites build their fixtures from this function, so they are asserting the
 * object the dashboard actually produces rather than one a test author
 * imagined.
 *
 * @param {Object} params
 * @param {Array<string>} params.supplementKeys - the stack's supplement keys
 * @param {Object} params.interactionsByKey - `groupInteractionsBySupplementKey` output
 * @param {Array} params.medications - the member's medications (`userProfile.medications`)
 * @returns {{hasInteractions: boolean, supplementInteractions: Object}}
 */
export const buildSupplementInteractions = ({
  supplementKeys = [],
  interactionsByKey = {},
  medications = [],
} = {}) => {
  const supplementInteractions = {};
  let hasInteractions = false;

  for (const key of supplementKeys) {
    const forKey = interactionsByKey[key] || [];
    if (!forKey.length) continue;

    const primary = selectHighestRiskInteraction(forKey);
    if (!primary?.risk || ['None', 'none'].includes(primary.risk)) continue;

    hasInteractions = true;
    supplementInteractions[key] = {
      // `riskChipLevel` (components/stack/RiskChip) guards on this as well as
      // `risk`, because saved stacks predating the interaction checker carry a
      // `risk` string with no finding behind it. Reaching here means there is
      // a real finding — without the flag the chip silently never renders, and
      // §8 forbids exactly that.
      has_interaction: true,
      risk: primary.risk,
      reasoning: primary.reasoning,
      medication_in_rxcui: primary.medication_in_rxcui,
      // What `StackCard`'s card-level modal reads: one row per supplement,
      // labelled with that supplement's highest-risk finding.
      medication_name: getMedicationDisplayName(primary.medication_in_rxcui, medications),
      confidence: primary.confidence,
      interactionCount: forKey.length,
      // And what `RecommendedStackCard`'s per-row chip reads. Resolved per
      // entry rather than copied from the primary: a supplement can interact
      // with several medications, and labelling every row with the worst one's
      // name would be a new wrong answer in place of the old one.
      allInteractions: forKey.map((entry) => ({
        ...entry,
        medication_name: getMedicationDisplayName(entry.medication_in_rxcui, medications),
      })),
    };
  }

  return { hasInteractions, supplementInteractions };
};
